import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import type { Product, ProductSummary } from '@/types/product';
import { mockProducts, mockCategories } from '@/mocks/products';

/** 检查 Supabase 是否已配置 */
const isSupabaseConnected = (): boolean => {
  const url = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
  const key = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;
  return Boolean(url && key && url.startsWith('https://'));
};

/** 将 mock 数据转换为 ProductSummary 格式（字段名统一） */
function normalizeMock(p: typeof mockProducts[0]): ProductSummary {
  return {
    id: p.id,
    name: p.name,
    product_code: (p as any).product_code,
    category: p.category,
    price: p.price,
    original_price: p.originalPrice,
    description: p.description,
    image: p.image,
    tags: p.tags,
    is_featured: p.isFeatured,
    is_visible: p.isVisible ?? true,
    specs: p.specs,
  };
}

/** 将 mock 数据转换为完整 Product 格式 */
function normalizeMockFull(p: typeof mockProducts[0]): Product {
  return {
    id: p.id,
    name: p.name,
    product_code: (p as any).product_code,
    category: p.category,
    price: p.price,
    original_price: p.originalPrice,
    description: p.description,
    long_description: p.longDescription,
    image: p.image,
    images: p.images,
    tags: p.tags,
    is_featured: p.isFeatured,
    is_visible: p.isVisible ?? true,
    specs: p.specs,
    highlights: p.highlights,
    faqs: p.faqs,
  };
}

interface UseProductsOptions {
  category?: string;
  featuredOnly?: boolean;
}

interface UseProductsResult {
  products: ProductSummary[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useProducts(options: UseProductsOptions = {}): UseProductsResult {
  const { category, featuredOnly } = options;
  const [products, setProducts] = useState<ProductSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const fetchProducts = async () => {
      setLoading(true);
      setError(null);

      // 未连接 Supabase，使用 mock 数据
      if (!isSupabaseConnected()) {
        let list = mockProducts.map(normalizeMock);
        list = list.filter((p) => p.is_visible !== false);
        if (featuredOnly) list = list.filter((p) => p.is_featured);
        if (category && category !== '全部') list = list.filter((p) => p.category === category);
        if (!cancelled) {
          setProducts(list);
          setLoading(false);
        }
        return;
      }

      try {
        let query = supabase
          .from('products')
          .select('id, name, product_code, category, price, original_price, description, image, tags, is_featured, is_visible, specs, translations')
          .eq('is_visible', true)
          .order('created_at', { ascending: false });

        if (featuredOnly) query = query.eq('is_featured', true);
        if (category && category !== '全部') query = query.eq('category', category);

        const { data, error: fetchError } = await query;

        if (cancelled) return;

        if (fetchError) {
          setError('加载产品失败，请稍后重试');
          setProducts([]);
        } else {
          setProducts((data as ProductSummary[]) ?? []);
        }
      } catch {
        if (!cancelled) {
          setError('网络错误，请检查连接后重试');
          setProducts([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchProducts();
    return () => { cancelled = true; };
  }, [category, featuredOnly, tick]);

  const refetch = () => setTick((t) => t + 1);

  return { products, loading, error, refetch };
}

interface UseProductResult {
  product: Product | null;
  loading: boolean;
  error: string | null;
}

export function useProduct(id: string): UseProductResult {
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    const fetchProduct = async () => {
      setLoading(true);
      setError(null);

      // 未连接 Supabase，使用 mock 数据
      if (!isSupabaseConnected()) {
        const found = mockProducts.find((p) => p.id === id);
        if (!cancelled) {
          setProduct(found ? normalizeMockFull(found) : null);
          if (!found) setError('产品不存在');
          setLoading(false);
        }
        return;
      }

      try {
        const { data, error: fetchError } = await supabase
          .from('products')
          .select('*')
          .eq('id', id)
          .maybeSingle();

        if (cancelled) return;

        if (fetchError) {
          setError('加载产品详情失败');
          setProduct(null);
        } else if (!data) {
          setError('产品不存在');
          setProduct(null);
        } else {
          setProduct(data as Product);
        }
      } catch {
        if (!cancelled) {
          setError('网络错误，请检查连接后重试');
          setProduct(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchProduct();
    return () => { cancelled = true; };
  }, [id]);

  return { product, loading, error };
}

export async function fetchCategories(): Promise<string[]> {
  if (!isSupabaseConnected()) return mockCategories;

  const { data, error } = await supabase
    .from('categories')
    .select('name')
    .order('sort_order', { ascending: true });

  if (error || !data || data.length === 0) return mockCategories;

  const names = data.map((d: { name: string }) => d.name);
  return ['全部', ...names];
}
